import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useProviderSlots, type ProviderSlotsResponse } from '@/services/scheduling/use-provider-slots';
import { useReminderProfilesList } from '@/services/scheduling/use-reminder-profiles-list';
import { reminderRules } from '@/services/scheduling/use-prebook-appointment-profile';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { ProviderPickerGrid } from '@/scheduling/components/shared/provider-picker-grid';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import {
  disciplineSpecialityIds,
  filterProvidersByDiscipline,
  noProviderForFilterText,
  warnIfPractitionerListMayBeCut,
} from '@/scheduling/lib/provider-discipline-filter';
import { useCreateAppointmentHandoff, type ProviderFilter } from '@/shared/store/create-appointment-handoff';
import { useAuthStore } from '@/auth/auth-store';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useDoctorsList } from '@/settings/services/use-doctors-list';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface ProviderSectionProps {
  organizationId: string;
  practiceId: string;
  /** Open the full-screen Provider Selection view (Flutter `searchProvider` step). */
  /** No longer used here: the search filters the cards in place (15 Oct). Kept so callers still compile. */
  onOpenSearch?: () => void;
  /**
   * Edit mode locks the provider — Flutter `provider_widget.dart` is
   * mounted with `showDelete: !widget.isEdit` (create_appointment_widget
   * .dart:1006), so the clear × is hidden and the provider can't be
   * changed once the appointment exists, REGARDLESS of status.
   */
  locked?: boolean;
  /** Referral handoffs only: list one discipline, and pick `preferredProviderId` when it is in that list. */
  providerFilter?: ProviderFilter;
}

export function ProviderSection({ organizationId, practiceId, locked = false, providerFilter }: ProviderSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const [specialtyFilter, setSpecialtyFilter] = useState<string | null>(null);
  // Live filter over the cards below (Antonin, 15 Oct: "it should just be a dynamic filter filtering the list of the
  // practitioners below"). Name or specialty, any order of words; combines with the specialty chip.
  const [search, setSearch] = useState('');
  const [specialtyMenuOpen, setSpecialtyMenuOpen] = useState(false);
  const selectedProviderId = watch('providerId');
  const selectedProviderName = watch('providerName');
  const selectedProviderSpeciality = watch('providerSpeciality');
  const selectedDate = watch('selectedDate');
  const location = watch('location');
  const reminderProfileId = watch('reminderProfileId');

  // Auto-populate the reminder profile from the practice's ReminderList
  // as soon as a provider is picked — Flutter parity with
  // `calendar_screen.dart:2256-2263` (after AppointmentReasonValueSet
  // returns, fires GetReminderProfilesEvent → picks first
  // enableInAppBooking and writes it to appointment.reminderProfile).
  // Profile pick later overrides this via PreBookSelectedAppointmentProfile.
  const reminderProfilesQuery = useReminderProfilesList(
    selectedProviderId ? practiceId : null,
  );
  useEffect(() => {
    if (reminderProfileId) return;
    const list = reminderProfilesQuery.data;
    if (!list?.length) return;
    const def = list.find((r) => r.enableInAppBooking) ?? list[0];
    if (!def?.id) return;
    setValue('reminderProfileId', def.id, { shouldValidate: true });
    setValue('reminderProfileName', def.profileName ?? null);
    setValue('reminderProfileRules', reminderRules(def));
    setValue('reminderProfile', def);
    // Intentionally narrow deps: re-run only when the fetched list changes,
    // not on every reminder edit (would clobber the user's manual override).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderProfilesQuery.data]);

  // Memoize the params object so its identity doesn't change every render.
  // Provider comes first in the flow (Antonin, 14 Oct) and LocationSelector
  // only mounts once a provider is picked, so the list must not wait for a
  // user pick. With none chosen yet it asks for the practice's own locations:
  // the same list LocationSelector offers (`useOrgLocations`). It never sends
  // `[]`, which is the hook's no-filter shape and returns every location in
  // the organisation. While that list is still loading — or the practice has
  // none — there is no request. A chosen location (edit mode) still narrows it.
  // The form's own local `selectedDate` is the day asked for: a UTC
  // `toISOString()` fallback asks for yesterday between 00:00 and 04:00 UAE.
  const locationId = location?.id;
  const { data: practiceLocations, isLoading: practiceLocationsLoading } = useOrgLocations(organizationId);
  const practiceLocationKey = (practiceLocations ?? []).map((l) => l.id).join(',');
  const queryParams = useMemo(() => {
    if (!practiceId || !organizationId || !selectedDate) {
      return null;
    }
    const locationIds = locationId
      ? [locationId]
      : practiceLocationKey.split(',').filter(Boolean);
    if (locationIds.length === 0) return null;
    return {
      organizationId,
      practiceId,
      date: selectedDate,
      locationIds,
    };
  }, [organizationId, practiceId, selectedDate, locationId, practiceLocationKey]);

  const { data: allProviders = [], isLoading: slotsLoading } = useProviderSlots(queryParams);
  // Waiting on the practice's locations reads as loading, not as "no providers".
  const isLoading = slotsLoading || (!locationId && practiceLocationsLoading);

  // PROD-1024 (F-SCH-132): a doctor/nurse creating an appointment sees only
  // themselves in the provider list; org-admin/staff see the full roster.
  // Mirrors Flutter create_appointment_widget.dart:2202-2210.
  const user = useAuthStore((s) => s.user);
  // Referral filter: ProviderSlots names only the main speciality, so the org practitioner list adds the others.
  // Fetched only for a referral booking; a failed read falls back to the main speciality alone.
  const doctorsQuery = useDoctorsList(providerFilter ? organizationId : null, false);
  // TanStack v5: a disabled query has isLoading=false, so this wait cannot hang without a filter.
  const doctorsSettled = !providerFilter || !doctorsQuery.isLoading;
  const doctorRows = doctorsQuery.data?.length ?? 0;
  useEffect(() => warnIfPractitionerListMayBeCut(doctorRows), [doctorRows]);
  const disciplineSpecialityIdSet = useMemo(
    () => (providerFilter ? disciplineSpecialityIds(doctorsQuery.data, providerFilter.discipline) : undefined),
    [doctorsQuery.data, providerFilter],
  );
  const providers = useMemo(
    () => filterProvidersByDiscipline(filterProvidersForCurrentUser(allProviders, user), providerFilter, disciplineSpecialityIdSet),
    [allProviders, user, providerFilter, disciplineSpecialityIdSet],
  );

  // Extract unique specialties from the loaded providers for the filter chips.
  const specialties = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of providers) {
      const display = specialityDisplay(p.speciality);
      if (display && !seen.has(display)) seen.set(display, display);
    }
    return [...seen.values()].sort();
  }, [providers]);

  // Filter providers by selected specialty (null = show all).
  const filteredProviders = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return providers.filter((p) => {
      if (specialtyFilter && specialityDisplay(p.speciality) !== specialtyFilter) return false;
      if (words.length === 0) return true;
      const hay = `${p.name ?? ''} ${specialityDisplay(p.speciality) ?? ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [providers, specialtyFilter, search]);

  const selectProvider = (provider: ProviderSlotsResponse) => {
    setValue('providerId', provider.id, { shouldValidate: true });
    setValue('providerName', provider.name);
    setValue('providerSpeciality', specialityDisplay(provider.speciality) ?? null);
    // Capture the SNOMED-shaped speciality Flutter ships on Book —
    // `{ specialitySkillId, code: { code, displayName, system, ... } }`.
    // Backend's gRPC GetSpecialitiesByCodesAsync looks the appointment up
    // by `code.code`, so a hardcoded "general" crashes the next read.
    // Wire payload uses `displayName`, but our `Coding` interface has
    // `display`; some endpoints ship one, some the other — accept both.
    const sCode = provider.speciality?.code as
      | { code?: string; display?: string; displayName?: string }
      | undefined;
    setValue(
      'providerSpecialityCode',
      sCode?.code
        ? {
            specialitySkillId: provider.specialityId,
            code: {
              code: sCode.code,
              displayName: sCode.displayName ?? sCode.display,
              system: 'https://snomed.info/sct',
              description: null,
              shortName: null,
            },
          }
        : null,
    );
  };

  // Field reset order matches pre-migration verbatim
  // (providerId → providerName → providerSpeciality → providerSpecialityCode).
  // ProviderPickerGrid invokes `onClear` as a single function call —
  // setValue order is preserved here. Important because downstream
  // watchers (slot-availability query, reminder-profile auto-pick)
  // observe `providerId` first; resetting in any other order risks
  // a stale intermediate snapshot during the render cycle.
  // Samer review §4 on PR #322.
  const clearProvider = () => {
    setValue('providerId', null);
    setValue('providerName', null);
    setValue('providerSpeciality', null);
    setValue('providerSpecialityCode', null);
  };

  // Referral handoff: pick the referred practitioner once, through the same path as a hand pick. Not in the
  // (filtered) list means no pick. The store clears the preferred provider after the first pick, so a cleared provider is never picked again.
  useEffect(() => {
    const wanted = providerFilter?.preferredProviderId;
    // Wait for the practitioner list: a second-speciality dietitian is only in `providers` once it has settled.
    if (!wanted || selectedProviderId || locked || !doctorsSettled) return;
    const match = providers.find((p) => p.id === wanted);
    if (!match) return;
    selectProvider(match);
    // One-shot for the whole handoff: this section unmounts when the full provider view opens.
    useCreateAppointmentHandoff.getState().consumePreferredProvider();
    // selectProvider only calls setValue; run when the list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, providerFilter?.preferredProviderId, doctorsSettled]);

  // Build the SelectedProviderInfo for ProviderPickerGrid out of the
  // RHF form values. We prefer `selectedProviderSpeciality` (the
  // already-formatted string Flutter ships through the form) but fall
  // back to looking up the speciality from the providers list — covers
  // the edit-flow case where the form was hydrated from an existing
  // appointment but the providers list hasn't fetched yet.
  const selectedInfo = selectedProviderId
    ? {
        id: selectedProviderId,
        name: selectedProviderName ?? '',
        photo: providers.find((p) => p.id === selectedProviderId)?.photo ?? null,
        speciality:
          selectedProviderSpeciality ??
          specialityDisplay(
            providers.find((p) => p.id === selectedProviderId)?.speciality,
          ) ??
          null,
      }
    : null;

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Provider
      </p>

      {/* Search + specialty, one row (Antonin, 15 Oct: "have a 'ALL' only, then i can click on it and it opens the list").
          The search filters the cards below as you type; the specialty is one "All ▾" control (it names the chosen
          specialty once picked) instead of a row of chips. */}
      {!selectedProviderId && !locked && (
        <div className="mb-3 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-hint pointer-events-none" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); } }}
              placeholder="Search providers"
              aria-label="Search providers"
              className="w-full h-10 pl-8 pr-3 rounded-lg border border-border-default bg-surface-card text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:outline-none"
            />
          </div>
          {specialties.length > 1 && (
            <Popover open={specialtyMenuOpen} onOpenChange={setSpecialtyMenuOpen}>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    aria-label={`Specialty: ${specialtyFilter ?? 'All'}`}
                    className={cn(
                      'inline-flex h-10 max-w-[200px] shrink-0 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors',
                      specialtyFilter
                        ? 'border-brand-primary bg-blue-100 text-brand-primary'
                        : 'border-border-default bg-surface-card text-text-primary hover:bg-grey-100',
                    )}
                  />
                }
              >
                <span className="truncate">{specialtyFilter ?? 'All'}</span>
                <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 gap-0 p-1">
                <div role="listbox" aria-label="Specialty" className="max-h-[320px] overflow-y-auto">
                  {[null, ...specialties].map((s) => {
                    const on = specialtyFilter === s;
                    return (
                      <button
                        key={s ?? 'all'}
                        type="button"
                        role="option"
                        aria-selected={on}
                        onClick={() => { setSpecialtyFilter(s); setSpecialtyMenuOpen(false); }}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-text-primary hover:bg-grey-100"
                      >
                        <span className="truncate">{s ?? 'All'}</span>
                        {on ? <Check className="h-4 w-4 shrink-0 text-brand-primary" aria-hidden /> : null}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      <ProviderPickerGrid
        providers={filteredProviders}
        selected={selectedInfo}
        isLoading={isLoading}
        onSelect={selectProvider}
        onClear={clearProvider}
        hideSearch
        searchValue={search}
        // While searching every match shows; the 4-card clamp is for browsing.
        collapsedCount={search.trim() ? Math.max(filteredProviders.length, 4) : undefined}
        {...(providerFilter ? { emptyText: noProviderForFilterText(providerFilter) } : {})}
        noMatchText={`No provider matches “${search.trim()}”.`}
        readOnly={locked}
      />
    </div>
  );
}
